import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Database, Play, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STAGE_BADGE = {
  1: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  2: 'bg-amber-50 text-amber-700 border-amber-200',
  3: 'bg-red-50 text-red-700 border-red-200',
};

const empty = {
  category: '', min_age_days: '', max_age_days: '', provisioning_percentage: '',
  liability_account: '', expense_account: '',
  ifrs9_stage: '', ecl_method: '12_month', pd_percent: '', lgd_percent: '',
};

const LoanProvisioningPage = () => {
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/loan-provisioning-criteria'); setCriteria(res.data || []); }
    catch (e) { setCriteria([]); }
    setLoading(false);
  };

  const openCreate = () => { setEditId(null); setForm(empty); setShowModal(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      category: c.category || '', min_age_days: String(c.min_age_days ?? ''), max_age_days: String(c.max_age_days ?? ''),
      provisioning_percentage: String(c.provisioning_percentage ?? ''),
      liability_account: c.liability_account || '', expense_account: c.expense_account || '',
      ifrs9_stage: c.ifrs9_stage ? String(c.ifrs9_stage) : '',
      ecl_method: c.ecl_method || '12_month',
      pd_percent: c.pd_percent != null ? String(c.pd_percent) : '',
      lgd_percent: c.lgd_percent != null ? String(c.lgd_percent) : '',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      min_age_days: parseInt(form.min_age_days) || 0,
      max_age_days: parseInt(form.max_age_days) || 0,
      provisioning_percentage: parseFloat(form.provisioning_percentage) || 0,
      ifrs9_stage: form.ifrs9_stage ? parseInt(form.ifrs9_stage) : null,
      pd_percent: form.pd_percent !== '' ? parseFloat(form.pd_percent) : null,
      lgd_percent: form.lgd_percent !== '' ? parseFloat(form.lgd_percent) : null,
    };
    try {
      if (editId) {
        await api.put(`/loan-provisioning-criteria/${editId}`, payload);
        toast.success('Criteria updated');
      } else {
        await api.post('/loan-provisioning-criteria', payload);
        toast.success('Criteria created');
      }
      setShowModal(false);
      load();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this criterion?')) return;
    try { await api.delete(`/loan-provisioning-criteria/${id}`); load(); }
    catch { toast.error('Delete failed'); }
  };

  const handleSeedIfrs9 = async () => {
    try {
      const r = await api.post('/loan-provisioning-criteria/seed-ifrs9');
      toast.success(r.data.message || `Seeded ${r.data.inserted} stages`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const handleRunClassifier = async () => {
    if (!window.confirm('Run IFRS-9 ECL classifier across the entire loan book? This will reclassify every active loan and post journal entries for any change in provision.')) return;
    setRunning(true);
    try {
      const r = await api.post('/loan-provisioning-criteria/run-classifier');
      setLastRun(r.data);
      toast.success(`Processed ${r.data.loans_processed} loan(s). ECL Δ posted: R ${r.data.delta_posted}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Classifier failed'); }
    setRunning(false);
  };

  return (
    <AdminSubPage title="Loan Provisioning Criteria (IFRS-9 ECL)" breadcrumb={['Admin', 'Organization', 'Loan Provisioning Criteria']} backPath="/dashboard/admin/organization">
      <div className="space-y-5">
        <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800">
          <strong>IFRS-9 Three-Stage ECL Model</strong>: each criterion below maps a Days-Past-Due (DPD) bucket to an IFRS-9 stage with a Probability of Default (PD) and Loss Given Default (LGD). The classifier computes <code>ECL = Outstanding × PD × LGD</code> per loan, posts the delta to <em>Allowance for ECL</em> / <em>Impairment Expense</em>, and records the loan's stage on its record.
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={openCreate} className="bg-[#1f1f1f] text-white" data-testid="add-criteria-btn"><Plus className="w-4 h-4 mr-1" />Add Criteria</Button>
          <Button onClick={handleSeedIfrs9} variant="outline" data-testid="seed-ifrs9-btn"><Database className="w-4 h-4 mr-1" />Seed IFRS-9 Defaults</Button>
          <Button onClick={handleRunClassifier} disabled={running} className="bg-[#C5A059] hover:bg-[#D4B16A] text-white" data-testid="run-classifier-btn">
            <Play className="w-4 h-4 mr-1" />{running ? 'Running...' : 'Run ECL Classifier'}
          </Button>
        </div>

        {lastRun && (
          <div className="bg-white border border-gray-200 p-4 text-xs space-y-1" data-testid="ecl-run-summary">
            <p className="font-semibold text-[#1f1f1f] mb-1">Last classifier run — {lastRun.duration_seconds}s</p>
            <p>Loans processed: <strong>{lastRun.loans_processed}</strong></p>
            <p>Stage 1 / 2 / 3: <strong>{lastRun.stages?.['1'] || 0}</strong> / <strong>{lastRun.stages?.['2'] || 0}</strong> / <strong>{lastRun.stages?.['3'] || 0}</strong></p>
            <p>Total ECL before: <strong>R {lastRun.total_ecl_before?.toLocaleString()}</strong> → after: <strong>R {lastRun.total_ecl_after?.toLocaleString()}</strong></p>
            <p>Δ posted to GL: <strong className={lastRun.delta_posted > 0 ? 'text-red-600' : lastRun.delta_posted < 0 ? 'text-emerald-600' : ''}>R {lastRun.delta_posted?.toLocaleString()}</strong> &middot; Journals posted: {lastRun.journals_posted}</p>
            {lastRun.warning && <p className="text-amber-700">{lastRun.warning}</p>}
          </div>
        )}

        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Category</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">DPD Range</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">IFRS-9 Stage</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">ECL Method</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">PD %</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">LGD %</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Provision %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr> :
                criteria.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-gray-400">No criteria defined. Click <em>Seed IFRS-9 Defaults</em> to start.</td></tr> :
                  criteria.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-[#1f1f1f]">{c.category}</td>
                      <td className="px-3 py-2 text-gray-600">{c.min_age_days}–{c.max_age_days === 9999 ? '∞' : c.max_age_days}</td>
                      <td className="px-3 py-2">{c.ifrs9_stage ? <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${STAGE_BADGE[c.ifrs9_stage]}`}>Stage {c.ifrs9_stage}</span> : <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{c.ecl_method === 'lifetime' ? 'Lifetime' : c.ecl_method === '12_month' ? '12-month' : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.pd_percent != null ? `${c.pd_percent}%` : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.lgd_percent != null ? `${c.lgd_percent}%` : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{c.provisioning_percentage}%</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800 mr-2" data-testid={`edit-${c.id}`}><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl" aria-describedby="lpc-desc">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Provisioning Criteria</DialogTitle></DialogHeader>
          <p id="lpc-desc" className="sr-only">Provisioning criteria form</p>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div><Label>Category Name *</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g., Stage 1 — Performing" data-testid="lpc-cat" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Min Days *</Label><Input type="number" value={form.min_age_days} onChange={e => setForm({ ...form, min_age_days: e.target.value })} /></div>
              <div><Label>Max Days *</Label><Input type="number" value={form.max_age_days} onChange={e => setForm({ ...form, max_age_days: e.target.value })} placeholder="9999 = ∞" /></div>
              <div><Label>Provision % *</Label><Input type="number" step="0.01" value={form.provisioning_percentage} onChange={e => setForm({ ...form, provisioning_percentage: e.target.value })} /></div>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">IFRS-9 ECL Parameters</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>IFRS-9 Stage</Label>
                  <Select value={form.ifrs9_stage} onValueChange={v => setForm({ ...form, ifrs9_stage: v })}>
                    <SelectTrigger data-testid="lpc-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Stage 1 — Performing</SelectItem>
                      <SelectItem value="2">Stage 2 — Significant Increase in Credit Risk</SelectItem>
                      <SelectItem value="3">Stage 3 — Credit-Impaired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>ECL Method</Label>
                  <Select value={form.ecl_method} onValueChange={v => setForm({ ...form, ecl_method: v })}>
                    <SelectTrigger data-testid="lpc-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12_month">12-month ECL (Stage 1)</SelectItem>
                      <SelectItem value="lifetime">Lifetime ECL (Stage 2 / 3)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>PD % (Probability of Default)</Label><Input type="number" step="0.01" value={form.pd_percent} onChange={e => setForm({ ...form, pd_percent: e.target.value })} placeholder="e.g., 2.0" data-testid="lpc-pd" /></div>
                <div><Label>LGD % (Loss Given Default)</Label><Input type="number" step="0.01" value={form.lgd_percent} onChange={e => setForm({ ...form, lgd_percent: e.target.value })} placeholder="e.g., 50.0" data-testid="lpc-lgd" /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="lpc-save">{editId ? 'Update' : 'Submit'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default LoanProvisioningPage;
